import { Capacitor } from "@capacitor/core";

// RevenueCat native in-app purchases (iOS + Android).
// Native purchases are confirmed independently by the backend before access.

const PRODUCT_ID = { monthly: "choremouse_monthly", annual: "choremouse_annual" };
const ENTITLEMENT = "plus";
const baseProductId = (identifier) => String(identifier || "").split(":", 1)[0];

let configuredFamily = null;
let purchasesRef = null;
let operationQueue = Promise.resolve();

export const isNativeBilling = () => Capacitor.isNativePlatform();

async function loadPlugin() {
  if (purchasesRef) return purchasesRef;
  // Dynamic import so the web bundle never pulls native code at runtime.
  const mod = await import("@revenuecat/purchases-capacitor");
  purchasesRef = mod.Purchases;
  return purchasesRef;
}

function keyForPlatform() {
  const key =
    Capacitor.getPlatform() === "ios"
      ? process.env.REACT_APP_REVENUECAT_IOS_API_KEY
      : process.env.REACT_APP_REVENUECAT_ANDROID_API_KEY;
  if (!key) throw new Error("Missing RevenueCat public SDK key for this platform");
  return key;
}

async function configureFamily(familyId) {
  if (!familyId) throw new Error("Sign in to your family before managing purchases.");
  const Purchases = await loadPlugin();
  const appUserID = String(familyId);
  if (!configuredFamily) {
    await Purchases.configure({ apiKey: keyForPlatform(), appUserID });
  } else if (configuredFamily !== appUserID) {
    await Purchases.logIn({ appUserID });
  }
  configuredFamily = appUserID;
  return Purchases;
}

// Keep identity changes and purchases in one queue. Switching families while a
// purchase is pending must not attribute that purchase to the wrong family.
function withFamily(familyId, action) {
  const operation = operationQueue.then(async () => action(await configureFamily(familyId)));
  operationQueue = operation.catch(() => {});
  return operation;
}

export async function initRevenueCat(familyId) {
  if (!isNativeBilling() || !familyId) return;
  return withFamily(familyId, () => undefined);
}

function stateFrom(info) {
  const plus = info?.entitlements?.active?.[ENTITLEMENT];
  const productId = baseProductId(plus?.productIdentifier);
  const plan = productId === PRODUCT_ID.annual ? "annual" : productId === PRODUCT_ID.monthly ? "monthly" : null;
  return { isPlus: plus?.isActive === true, plan };
}

export async function getSubscriptionState(familyId) {
  if (!isNativeBilling()) return { isPlus: false, plan: null };
  return withFamily(familyId, async (Purchases) => {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return stateFrom(customerInfo);
  });
}

export async function getNativePlans(familyId) {
  if (!isNativeBilling()) return null;
  return withFamily(familyId, async (Purchases) => {
    const offerings = await Purchases.getOfferings();
    const packages = offerings?.current?.availablePackages ?? [];
    const plans = {};
    Object.entries(PRODUCT_ID).forEach(([plan, productId]) => {
      const product = packages.find((pkg) => baseProductId(pkg.product.identifier) === productId)?.product;
      if (product?.priceString) plans[plan] = { priceString: product.priceString };
    });
    return plans;
  });
}

export async function purchasePlanNative(familyId, plan) {
  if (!isNativeBilling()) throw new Error("Native purchases require the iPhone or Android app.");
  if (!Object.prototype.hasOwnProperty.call(PRODUCT_ID, plan)) throw new Error("Choose a monthly or annual plan.");
  return withFamily(familyId, async (Purchases) => {
    const offerings = await Purchases.getOfferings();
    const packages = offerings?.current?.availablePackages ?? [];
    const wanted = PRODUCT_ID[plan];
    const pkg = packages.find((p) => baseProductId(p.product.identifier) === wanted);
    if (!pkg) throw new Error("This plan isn't available in the store yet. Please try again later.");
    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
      return stateFrom(customerInfo);
    } catch (e) {
      if (e?.userCancelled || String(e?.code) === "1" || e?.code === "PURCHASE_CANCELLED") {
        return { isPlus: false, plan: null, cancelled: true };
      }
      throw e;
    }
  });
}

export async function restoreNative(familyId) {
  if (!isNativeBilling()) return { isPlus: false, plan: null };
  return withFamily(familyId, async (Purchases) => {
    const { customerInfo } = await Purchases.restorePurchases();
    return stateFrom(customerInfo);
  });
}
